import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { API_URL } from '../utils/api';
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight, Truck, Lock } from 'lucide-react';
import { toast } from 'sonner';

export const CartDrawer = () => {
  const navigate = useNavigate();
  const {
    cartItems, updateCartItem, removeFromCart, cartTotal, cartCount,
    drawerOpen, closeDrawer,
    FREE_SHIPPING_THRESHOLD, shippingProgress, shippingRemaining
  } = useCart();

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const handleQuantityChange = async (productId, size, newQty) => {
    if (newQty < 1) return;
    try {
      await updateCartItem(productId, size, newQty);
    } catch {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (productId, size) => {
    try {
      await removeFromCart(productId, size);
      toast.success('Item removed');
    } catch {
      toast.error('Failed to remove item');
    }
  };

  const handleCheckout = () => {
    closeDrawer();
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    closeDrawer();
    navigate('/shop');
  };

  if (!drawerOpen) return null;

  const getImageUrl = (item) => {
    const img = item.product?.images?.[0];
    if (!img) return 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=200';
    return img.startsWith('http') ? img : `${API_URL}${img}`;
  };

  return (
    <div className="fixed inset-0 z-[100]" data-testid="cart-drawer">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px] animate-fade-in"
        onClick={closeDrawer}
        data-testid="cart-drawer-overlay"
      />

      {/* Drawer Panel */}
      <div
        className="absolute top-0 right-0 bottom-0 w-full sm:w-[420px] bg-white shadow-2xl flex flex-col animate-drawer-in"
        data-testid="cart-drawer-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 h-16 border-b border-[#E2E2E2] flex-shrink-0">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            <h2 className="text-sm font-semibold uppercase tracking-[0.1em]" data-testid="cart-drawer-title">
              Your Bag ({cartCount})
            </h2>
          </div>
          <button
            onClick={closeDrawer}
            className="w-10 h-10 flex items-center justify-center hover:bg-[#F9F8F6] rounded-full transition-colors btn-icon"
            data-testid="cart-drawer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Free Shipping Progress */}
        {cartItems.length > 0 && (
          <div className="px-6 py-3 bg-[#F9F8F6] border-b border-[#E2E2E2]" data-testid="shipping-progress">
            <div className="flex items-center gap-2 mb-1.5">
              <Truck className="w-4 h-4 text-[#C9A84C]" strokeWidth={1.5} />
              <span className="text-xs font-medium text-[#666]">
                {shippingRemaining > 0
                  ? <span>You're <strong className="text-[#0A0A0A]">&pound;{shippingRemaining.toFixed(2)}</strong> away from free shipping</span>
                  : <span className="text-green-700 font-semibold">You qualify for free UK shipping!</span>
                }
              </span>
            </div>
            <div className="w-full h-1.5 bg-[#E2E2E2] rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${shippingRemaining > 0 ? 'bg-[#C9A84C]' : 'bg-green-600'}`}
                style={{ width: `${shippingProgress}%` }}
                data-testid="shipping-progress-bar"
              />
            </div>
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-6 text-center" data-testid="cart-drawer-empty">
              <ShoppingBag className="w-12 h-12 text-[#E2E2E2] mb-4" strokeWidth={1} />
              <p className="text-base font-semibold mb-1">Your cart is empty</p>
              <p className="text-sm text-[#666] mb-6">Go find something you'll wear forever.</p>
              <button
                onClick={handleContinueShopping}
                className="btn-primary flex items-center gap-2 px-8"
                data-testid="cart-drawer-shop-btn"
              >
                Shop Now <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="divide-y divide-[#E2E2E2]">
              {cartItems.map((item) => (
                <div
                  key={`${item.product_id}-${item.size}`}
                  className="flex gap-4 p-4 sm:p-5"
                  data-testid={`cart-drawer-item-${item.product_id}`}
                >
                  {/* Image */}
                  <div className="w-20 h-24 bg-[#F0EEEB] overflow-hidden flex-shrink-0">
                    <img
                      src={getImageUrl(item)}
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width={80}
                      height={96}
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <p className="text-sm font-semibold truncate" data-testid={`cart-drawer-item-name-${item.product_id}`}>
                        {item.product?.name}
                      </p>
                      <p className="text-[11px] text-[#666] mt-0.5">
                        Size: {item.size}
                        {item.product?.condition_grade && <span> · {item.product.condition_grade}</span>}
                      </p>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      {/* Quantity Stepper */}
                      <div className="flex items-center border border-[#E2E2E2]" data-testid={`cart-drawer-qty-${item.product_id}`}>
                        <button
                          onClick={() => handleQuantityChange(item.product_id, item.size, item.quantity - 1)}
                          className="w-9 h-9 flex items-center justify-center hover:bg-[#F9F8F6] transition-colors btn-icon"
                          data-testid={`cart-drawer-decrease-${item.product_id}`}
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-semibold" data-testid={`cart-drawer-quantity-${item.product_id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item.product_id, item.size, item.quantity + 1)}
                          className="w-9 h-9 flex items-center justify-center hover:bg-[#F9F8F6] transition-colors btn-icon"
                          data-testid={`cart-drawer-increase-${item.product_id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Price */}
                      <p className="text-sm font-bold" data-testid={`cart-drawer-line-price-${item.product_id}`}>
                        &pound;{((item.product?.price || 0) * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Remove */}
                  <button
                    onClick={() => handleRemove(item.product_id, item.size)}
                    className="self-start w-8 h-8 flex items-center justify-center text-[#999] hover:text-red-500 transition-colors btn-icon"
                    data-testid={`cart-drawer-remove-${item.product_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with subtotal + checkout */}
        {cartItems.length > 0 && (
          <div className="border-t border-[#E2E2E2] p-5 sm:p-6 flex-shrink-0 bg-white" data-testid="cart-drawer-footer">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold uppercase tracking-[0.08em]">Subtotal</span>
              <span className="text-lg font-bold" data-testid="cart-drawer-subtotal">
                &pound;{cartTotal.toFixed(2)}
              </span>
            </div>
            <button
              onClick={handleCheckout}
              className="w-full bg-[#0A0A0A] text-white text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#1a1a1a] transition-colors flex items-center justify-center gap-2"
              style={{ minHeight: '52px' }}
              data-testid="cart-drawer-checkout-btn"
            >
              Checkout <ArrowRight className="w-4 h-4" />
            </button>
            {/* Trust line below checkout */}
            <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="cart-drawer-trust-line">
              <Lock className="w-3 h-3 text-[#999]" strokeWidth={1.5} />
              <span className="text-[10px] text-[#999]">Secure checkout &middot; Free returns &middot; Dispatched in 48hrs</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
